import { useState, useRef, useLayoutEffect, RefObject } from "react";

/**
 * Custom hook to measure and track the height of a DOM element.
 * Automatically updates when the element resizes.
 *
 * @returns A tuple containing the ref to attach to the element and its current height in pixels
 *
 * @example
 * const [elementRef, height] = useElementHeight();
 * return <div ref={elementRef}>Height: {height}px</div>;
 */
export default function useElementHeight(): [
  RefObject<HTMLDivElement>,
  number,
] {
  const elementRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>(0);

  useLayoutEffect(() => {
    const updateHeight = (): void => {
      if (elementRef.current) {
        setHeight(elementRef.current.clientHeight);
      }
    };

    // Initial measurement
    updateHeight();

    // Observe size changes
    const resizeObserver = new ResizeObserver(updateHeight);
    if (elementRef.current) {
      resizeObserver.observe(elementRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return [elementRef, height];
}
